<template>
  <div class="markdown-editor-wrap" :class="{ 'markdown-editor-wrap--focused': isFocused }">
    <div style="position: relative">
      <div v-if="label && outlined" style="position: absolute; height: 0" class="v-text-field__slot">
        <label
          style="position: relative; left: 10px; bottom: 11px; z-index: 1; font-size: 12px"
          :style="`${label ? 'padding: 2px;' : ''} background-color: ${
            !$vuetify.theme.dark ? 'white' : 'rgb(30, 30, 30)'
          };`"
          class="v-label"
          :class="isFocused ? 'primary--text' : ''"
        >
          {{ label }}
        </label>
      </div>
      <div v-else-if="label" class="v-label theme--light markdown-editor-label">
        {{ label }}
      </div>

      <!-- Formatting toolbar -->
      <div
        v-if="editor && !readOnly"
        class="markdown-toolbar d-flex align-center flex-wrap px-1"
        :class="{ 'markdown-toolbar--focused': isFocused }"
      >
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              v-bind="attrs"
              v-on="on"
              :class="{ 'toolbar-btn--active': editor.isActive('heading', { level: 1 }) }"
              @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
            >
              <v-icon small>mdi-format-header-1</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("components.utility.MarkdownEditor.heading1") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              v-bind="attrs"
              v-on="on"
              :class="{ 'toolbar-btn--active': editor.isActive('heading', { level: 2 }) }"
              @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
            >
              <v-icon small>mdi-format-header-2</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("components.utility.MarkdownEditor.heading2") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              v-bind="attrs"
              v-on="on"
              :class="{ 'toolbar-btn--active': editor.isActive('heading', { level: 3 }) }"
              @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
            >
              <v-icon small>mdi-format-header-3</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("components.utility.MarkdownEditor.heading3") }}</span>
        </v-tooltip>

        <v-divider vertical class="mx-1 toolbar-divider" />

        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              v-bind="attrs"
              v-on="on"
              :class="{ 'toolbar-btn--active': editor.isActive('bold') }"
              @click="editor.chain().focus().toggleBold().run()"
            >
              <v-icon small>mdi-format-bold</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("components.utility.MarkdownEditor.bold") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              v-bind="attrs"
              v-on="on"
              :class="{ 'toolbar-btn--active': editor.isActive('italic') }"
              @click="editor.chain().focus().toggleItalic().run()"
            >
              <v-icon small>mdi-format-italic</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("components.utility.MarkdownEditor.italic") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              v-bind="attrs"
              v-on="on"
              :class="{ 'toolbar-btn--active': editor.isActive('underline') }"
              @click="editor.chain().focus().toggleUnderline().run()"
            >
              <v-icon small>mdi-format-underline</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("components.utility.MarkdownEditor.underline") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              v-bind="attrs"
              v-on="on"
              :class="{ 'toolbar-btn--active': editor.isActive('strike') }"
              @click="editor.chain().focus().toggleStrike().run()"
            >
              <v-icon small>mdi-format-strikethrough</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("components.utility.MarkdownEditor.strikethrough") }}</span>
        </v-tooltip>

        <v-divider vertical class="mx-1 toolbar-divider" />

        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              v-bind="attrs"
              v-on="on"
              :class="{ 'toolbar-btn--active': editor.isActive('blockquote') }"
              @click="editor.chain().focus().toggleBlockquote().run()"
            >
              <v-icon small>mdi-format-quote-close</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("components.utility.MarkdownEditor.quote") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              v-bind="attrs"
              v-on="on"
              :class="{ 'toolbar-btn--active': editor.isActive('code') }"
              @click="editor.chain().focus().toggleCode().run()"
            >
              <v-icon small>mdi-marker</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("components.utility.MarkdownEditor.highlight") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              v-bind="attrs"
              v-on="on"
              :class="{ 'toolbar-btn--active': editor.isActive('codeBlock') }"
              @click="editor.chain().focus().toggleCodeBlock().run()"
            >
              <v-icon small>mdi-format-list-group</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("components.utility.MarkdownEditor.section") }}</span>
        </v-tooltip>

        <v-divider vertical class="mx-1 toolbar-divider" />

        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              v-bind="attrs"
              v-on="on"
              :class="{ 'toolbar-btn--active': editor.isActive('bulletList') }"
              @click="editor.chain().focus().toggleBulletList().run()"
            >
              <v-icon small>mdi-format-list-bulleted</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("components.utility.MarkdownEditor.bulletList") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              v-bind="attrs"
              v-on="on"
              :class="{ 'toolbar-btn--active': editor.isActive('orderedList') }"
              @click="editor.chain().focus().toggleOrderedList().run()"
            >
              <v-icon small>mdi-format-list-numbered</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("components.utility.MarkdownEditor.numberedList") }}</span>
        </v-tooltip>

        <template v-if="canUploadImage">
          <v-divider vertical class="mx-1 toolbar-divider" />
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn icon x-small v-bind="attrs" v-on="on" :loading="isUploading" @click="triggerImageUpload">
                <v-icon small>mdi-image-plus</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("components.utility.MarkdownEditor.uploadImage") }}</span>
          </v-tooltip>
        </template>
      </div>

      <editor-content
        :editor="editor"
        class="markdown-editor rounded"
        :class="{
          'markdown-editor--readonly': readOnly,
          'markdown-editor--focused': isFocused,
          'markdown-editor--with-toolbar': editor && !readOnly,
          'markdown-editor--flat': !outlined,
          'markdown-editor--dense': dense,
          'markdown-editor--dragover': isDragOver
        }"
        @click.native="onEditorClick"
        @dragover.native="onDragOver"
        @dragleave.native="onDragLeave"
        @drop.native="onDrop"
      />
      <input
        ref="imageFileInput"
        type="file"
        :accept="imageInputTypes"
        style="display: none"
        @change="onFileInputChange"
      />
    </div>
    <div v-if="hint" class="v-messages mt-1 ml-3" :class="$vuetify.theme.dark ? 'theme--dark' : 'theme--light'">
      <div class="v-messages__message">{{ hint }}</div>
    </div>
    <div
      v-if="enableMentions && !readOnly"
      class="mention-hint text-caption mt-1 ml-1"
      v-html="$t('components.utility.MarkdownEditor.mentionHint')"
    />
  </div>
</template>

<script lang="ts">
import Underline from "@tiptap/extension-underline";
import StarterKit from "@tiptap/starter-kit";
import { AnyExtension } from "@tiptap/core";
import { Editor, EditorContent } from "@tiptap/vue-2";
import { Markdown } from "tiptap-markdown";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { UserMention } from "./tiptap/UserMention";
import { ReferenceMention } from "./tiptap/ReferenceMention";
import { extractMentionRefs, IMentionRef } from "./tiptap/extract-refs";
import { MentionType } from "./tiptap/mention-type.enum";
import { fetchUserSuggestions, fetchRefSuggestions } from "./tiptap/mention-data-providers";
import { PartnerModule } from "@/store/modules/partner";
import { PartnerImageExtension, PARTNER_IMAGE_PREFIX } from "./tiptap/PartnerImageExtension";
import { PartnerImageModule } from "@/store/modules/partner-image.store";
import { ReportImageType } from "@/models/Report/ReportImageType";
import { UserModule } from "@/store/modules/me-user.store";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { ResourceEnum } from "@/store/enum/authResourceEnum";
import { ConfigModule } from "@/store/modules/config";

/** Max file size for image uploads in bytes (5 MB) */
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

@Component({
  components: { EditorContent }
})
export default class MarkdownEditor extends Vue {
  @Prop({ default: "" })
  value!: string;

  @Prop()
  label?: string;

  @Prop()
  hint?: string;

  @Prop({ default: false })
  readOnly!: boolean;

  @Prop({ default: true, type: Boolean })
  outlined!: boolean;

  @Prop({ default: false, type: Boolean })
  dense!: boolean;

  @Prop({ default: true, type: Boolean })
  enableMentions!: boolean;

  /** Partner ID required for image uploads. When set, enables image features. */
  @Prop()
  partnerId?: string;

  editor: Editor | null = null;
  isFocused = false;
  isUploading = false;
  isDragOver = false;

  /** Guard against re-entrant updates from onUpdate → $emit → watcher */
  private updating = false;

  /** The last set of extracted mention refs, exposed for parent components */
  mentionRefs: IMentionRef[] = [];

  get canUploadImage(): boolean {
    if (!this.partnerId) return false;
    return UserModule.abilities.can(ActionEnum.CREATE, ResourceEnum.IMAGE, this.partnerId);
  }

  get imageInputTypes(): string {
    return ConfigModule.imageInputTypesString;
  }

  mounted() {
    const extensions: AnyExtension[] = [StarterKit, Underline, Markdown as unknown as AnyExtension];

    if (this.enableMentions) {
      extensions.push(
        UserMention.configure({ fetchSuggestions: fetchUserSuggestions }),
        ReferenceMention.configure({ fetchSuggestions: fetchRefSuggestions })
      );
    }

    if (this.partnerId) {
      extensions.push(PartnerImageExtension.configure({ inline: true }));
    }

    this.editor = new Editor({
      content: this.value || "",
      extensions,
      editable: !this.readOnly,
      onUpdate: ({ editor }) => {
        this.updating = true;
        this.$emit("input", editor.storage.markdown.getMarkdown());
        if (this.enableMentions) {
          this.mentionRefs = extractMentionRefs(editor);
          this.$emit("refs-extracted", this.mentionRefs);
        }
        this.$nextTick(() => {
          this.updating = false;
        });
      },
      onFocus: () => {
        this.isFocused = true;
      },
      onBlur: () => {
        this.isFocused = false;
      }
    });
  }

  /** Open the native file picker for image upload */
  triggerImageUpload() {
    (this.$refs.imageFileInput as HTMLInputElement)?.click();
  }

  /** Handle file selection from the file input */
  onFileInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.uploadAndInsertImage(file);
    }
    input.value = "";
  }

  /* ---------- Drag & Drop ---------------------------------------- */

  onDragOver(event: DragEvent) {
    if (!this.canUploadImage) return;
    if (event.dataTransfer?.types.includes("Files")) {
      event.preventDefault();
      this.isDragOver = true;
    }
  }

  onDragLeave() {
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    this.isDragOver = false;
    if (!this.canUploadImage) return;

    const file = event.dataTransfer?.files?.[0];
    if (file && file.type.startsWith("image/")) {
      event.preventDefault();
      this.uploadAndInsertImage(file);
    }
  }

  /* ---------- Upload & Insert ------------------------------------ */

  async uploadAndInsertImage(file: File) {
    if (!this.canUploadImage || !this.partnerId || !this.editor) return;

    if (file.size > MAX_IMAGE_SIZE) {
      this.$toast.error(this.$t("components.utility.MarkdownEditor.imageTooLarge").toString());
      return;
    }

    if (!file.type.startsWith("image/")) {
      this.$toast.error(this.$t("components.utility.MarkdownEditor.invalidImageType").toString());
      return;
    }

    this.isUploading = true;
    try {
      const image = await PartnerImageModule.create({
        partnerId: this.partnerId,
        file,
        type: ReportImageType.others
      });

      this.editor
        .chain()
        .focus()
        .setImage({ src: `${PARTNER_IMAGE_PREFIX}${image.id}`, alt: file.name })
        .run();
    } catch (error) {
      this.$log.error("Image upload failed", error);
      this.$toast.error(this.$t("components.utility.MarkdownEditor.uploadFailed").toString());
    } finally {
      this.isUploading = false;
    }
  }

  @Watch("value")
  onValueChange(val: string) {
    if (this.updating || !this.editor) return;
    const current = this.editor.storage.markdown.getMarkdown();
    if (val !== current) {
      this.editor.commands.setContent(val || "", false);
    }
  }

  @Watch("readOnly")
  onReadOnlyChange(val: boolean) {
    this.editor?.setEditable(!val);
  }

  /**
   * Handle clicks on mention chips in read-only mode.
   * Navigates to the detail page for the referenced entity.
   * Also emits 'mention-click' with { refType, refId }.
   */
  onEditorClick(event: MouseEvent) {
    if (!this.readOnly) return;

    const target = event.target as HTMLElement;
    const mention = target.closest(
      `[data-type='${MentionType.USER}'], [data-type='${MentionType.REF}']`
    ) as HTMLElement | null;
    if (!mention) return;

    const dataType = mention.getAttribute("data-type");
    const id = mention.getAttribute("data-id");
    if (!id) return;

    const refType = dataType === MentionType.USER ? "user" : (mention.getAttribute("data-ref-type") ?? "");
    this.$emit("mention-click", { refType, refId: id });

    // RefMention navigation is handled by RefMentionNodeView (with .stop).
    // Only handle userMention navigation here since it has no custom node view.
    if (dataType !== MentionType.USER) return;

    const partnerId = PartnerModule.partner._id || PartnerModule.partner.id;
    if (!partnerId) return;

    // userMention clicks currently only emit — no route to navigate to.
    // Add navigation here if a user detail route becomes available.
  }

  beforeDestroy() {
    this.editor?.destroy();
  }
}
</script>

<style scoped>
.markdown-toolbar {
  border: 1px solid rgba(0, 0, 0, 0.42);
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  padding: 6px 4px;
  gap: 2px;
}

.theme--dark .markdown-toolbar {
  border-color: rgba(255, 255, 255, 0.7);
}

.markdown-toolbar--focused {
  border-color: var(--v-primary-base, #1976d2);
  border-width: 2px 2px 0 2px;
  padding: 6px 4px;
}

.toolbar-btn--active {
  background-color: rgba(0, 0, 0, 0.08);
}

.theme--dark .toolbar-btn--active {
  background-color: rgba(255, 255, 255, 0.15);
}

.toolbar-divider {
  max-height: 20px;
  align-self: center;
}

.markdown-editor--with-toolbar {
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;
}

.markdown-editor {
  border: 1px solid rgba(0, 0, 0, 0.42);
  border-radius: 4px;
  padding: 10px 12px;
  min-height: 150px;
  font-family: "Roboto", sans-serif;
  font-size: 16px;
  line-height: 1.75rem;
  letter-spacing: normal;
  color: rgba(0, 0, 0, 0.87);
  transition: border 0.15s;
}

.theme--dark .markdown-editor {
  border-color: rgba(255, 255, 255, 0.7);
  color: rgba(255, 255, 255, 0.87);
}

.markdown-editor:hover:not(.markdown-editor--focused):not(.markdown-editor--readonly) {
  border-color: rgba(0, 0, 0, 0.87);
}

.theme--dark .markdown-editor:hover:not(.markdown-editor--focused):not(.markdown-editor--readonly) {
  border-color: rgba(255, 255, 255, 1);
}

.markdown-editor--focused {
  border-color: var(--v-primary-base, #1976d2);
  border-width: 2px;
  padding: 9px 11px;
}

.markdown-editor--readonly {
  border-color: rgba(0, 0, 0, 0.12);
  padding: 0;
}

.markdown-editor--flat {
  border: none;
}

.markdown-editor-label {
  font-size: 12px;
  margin-bottom: 4px;
}

.markdown-editor--readonly >>> .ProseMirror a {
  pointer-events: auto;
  cursor: pointer;
}

.theme--dark .markdown-editor--readonly {
  border-color: rgba(255, 255, 255, 0.12);
}

.markdown-editor >>> .ProseMirror {
  outline: none;
  min-height: 134px;
}

.markdown-editor >>> .ProseMirror p {
  margin: 0 0 10px 0;
}

/** dense: remove all spacing and min-height — must come after base .ProseMirror rules */
.markdown-editor--dense {
  padding: 0;
  min-height: unset;
}

.markdown-editor--dense >>> div.ProseMirror {
  padding: 0 !important;
  margin: 0 !important;
  min-height: unset !important;
}

.markdown-editor--dense >>> div.ProseMirror p:last-child {
  margin-bottom: 0;
}

.markdown-editor >>> .ProseMirror h1 {
  font-size: 1.6em;
  font-weight: 500;
  line-height: 1.5;
  margin: 24px 0 16px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  padding-bottom: 0.3rem;
  line-height: 0.8;
}

.theme--dark .markdown-editor >>> .ProseMirror h1 {
  border-bottom-color: rgba(255, 255, 255, 0.1);
}

.markdown-editor >>> .ProseMirror h2 {
  font-size: 1.35em;
  font-weight: 500;
  line-height: 1.5;
  margin: 24px 0 16px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  padding-bottom: 0.3rem;
  line-height: 1;
}

.theme--dark .markdown-editor >>> .ProseMirror h2 {
  border-bottom-color: rgba(255, 255, 255, 0.1);
}

.markdown-editor >>> .ProseMirror h3 {
  font-size: 1.15em;
  font-weight: 500;
  line-height: 1.5;
  margin: 10px 0 6px 0;
}

.markdown-editor >>> .ProseMirror h4,
.markdown-editor >>> .ProseMirror h5,
.markdown-editor >>> .ProseMirror h6 {
  font-size: 1em;
  font-weight: 500;
  line-height: 1.5;
  margin: 10px 0 4px 0;
}

.markdown-editor >>> .ProseMirror ul,
.markdown-editor >>> .ProseMirror ol {
  padding-left: 24px;
  margin: 0 0 10px 0;
}

.markdown-editor >>> .ProseMirror li {
  margin-bottom: 2px;
  line-height: 1.2;
}

.markdown-editor >>> .ProseMirror li > p {
  margin: 0;
}

.markdown-editor >>> .ProseMirror blockquote {
  border-left: 3px solid rgba(0, 0, 0, 0.12);
  margin: 0 0 10px 0;
  padding-left: 12px;
  color: rgba(0, 0, 0, 0.6);
  line-height: 1.5;
}

.markdown-editor >>> .ProseMirror blockquote p {
  margin-bottom: 0;
}

.theme--dark .markdown-editor >>> .ProseMirror blockquote {
  border-left-color: rgba(255, 255, 255, 0.24);
  color: rgba(255, 255, 255, 0.6);
}

.markdown-editor >>> .ProseMirror code {
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 3px;
  padding: 2px 4px;
  font-size: 0.875em;
}

.theme--dark .markdown-editor >>> .ProseMirror code {
  background-color: rgba(255, 255, 255, 0.1);
}

.markdown-editor >>> .ProseMirror pre code {
  background-color: transparent;
  padding: 0;
  font-size: inherit;
}

.markdown-editor >>> .ProseMirror pre {
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  padding: 8px 12px;
  margin: 0 0 10px 0;
  font-size: 0.875em;
  line-height: 1.5;
}

.theme--dark .markdown-editor >>> .ProseMirror pre {
  background-color: rgba(255, 255, 255, 0.1);
}

.markdown-editor >>> .ProseMirror hr {
  border: none;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  margin: 0 0 10px 0;
}

.theme--dark .markdown-editor >>> .ProseMirror hr {
  border-top-color: rgba(255, 255, 255, 0.24);
}

/* Mention chip styles */
.markdown-editor >>> .ProseMirror .mention--user {
  background-color: var(--v-primary-lighten5, rgba(25, 118, 210, 0.12));
  color: var(--v-primary-base, #1976d2);
  border-radius: 4px;
  padding: 1px 4px;
  font-weight: 500;
  white-space: nowrap;
}

.markdown-editor >>> .ProseMirror .mention--ref {
  background-color: rgba(0, 0, 0, 0.08);
  color: rgba(0, 0, 0, 0.6);
  border-radius: 4px;
  padding: 1px 4px;
  font-weight: 500;
  white-space: nowrap;
}

.theme--dark .markdown-editor >>> .ProseMirror .mention--ref {
  background-color: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.7);
}

.markdown-editor--readonly >>> .ProseMirror .mention--user,
.markdown-editor--readonly >>> .ProseMirror .mention--ref {
  cursor: pointer;
}

.markdown-editor--readonly >>> .ProseMirror .mention--user:hover,
.markdown-editor--readonly >>> .ProseMirror .mention--ref:hover {
  text-decoration: underline;
}

.mention-hint {
  color: rgba(0, 0, 0, 0.6);
  font-size: 12px;
}

.theme--dark .mention-hint {
  color: rgba(255, 255, 255, 0.5);
}

/* Drag-over visual feedback */
.markdown-editor--dragover {
  border-color: var(--v-primary-base, #1976d2) !important;
  border-style: dashed !important;
  background-color: rgba(25, 118, 210, 0.04);
}

/* Partner image styles inside editor */
.markdown-editor >>> .ProseMirror img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  margin: 4px 0;
}
</style>
